/**
 * NotificationsPage — `/notifications`  (superall-shell-04)
 *
 * Renders a per-BU activity card for every BU the user has visited.
 * Data source: GET /api/platform/v1/activity/summary (60 s poll, already
 * wired in useActivitySummary).
 *
 * Each card shows:
 *   • BU name + icon (from BU_SURFACES directory)
 *   • Unread count badge (omitted when 0)
 *   • Last-activity relative time
 *   • "Open" button → handoffToBU
 *
 * Unknown BU ids (from server but not in BU_SURFACES) are rendered with a
 * generic icon so the surface degrades cleanly as new BUs are added.
 */

import * as React from 'react';
import { StaggerList, StaggerItem, AnimatedNumber, FadeIn } from '@workspace/ui';

import { useActivitySummary } from '../hooks/useActivity';
import { BU_SURFACES, handoffToBU, formatRelative, type BUSurface } from '../lib/handoff';
import { useAuth } from '../providers/AuthProvider';
import { useLang, useT } from '../providers/LangProvider';

// ── helpers ──────────────────────────────────────────────────────────────────

function buById(id: string): BUSurface | undefined {
  return BU_SURFACES.find((b) => b.id === id);
}

// ── BU activity card ─────────────────────────────────────────────────────────

interface BUCardProps {
  buId: string;
  unreadCount: number;
  lastActivityAt: string | null;
}

function BUActivityCard({ buId, unreadCount, lastActivityAt }: BUCardProps) {
  const t = useT();
  const [lang] = useLang();
  const { token } = useAuth();
  const bu = buById(buId);
  const [opening, setOpening] = React.useState(false);
  const [handoffErr, setHandoffErr] = React.useState<string | null>(null);

  const nameEn = bu?.nameEn ?? buId;
  const nameAr = bu?.nameAr ?? buId;
  const name = lang === 'ar' ? nameAr : nameEn;
  const icon = bu?.icon ?? '🔗';
  const isLive = bu?.status !== 'soon';
  const relative = formatRelative(lastActivityAt, lang);

  async function openBU() {
    if (!bu || !token || !isLive) return;
    setOpening(true);
    setHandoffErr(null);
    try {
      await handoffToBU(bu, token, import.meta.env.DEV);
    } catch (err) {
      setHandoffErr(err instanceof Error ? err.message : t.handoffError);
    } finally {
      setOpening(false);
    }
  }

  return (
    <li className="flex items-center gap-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-4 py-4">
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-neutral-100 dark:bg-neutral-800 text-2xl"
        aria-hidden="true"
      >
        {icon}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-semibold text-neutral-900 dark:text-neutral-100">{name}</p>
          {unreadCount > 0 && (
            <span
              aria-label={`${unreadCount} ${lang === 'ar' ? t.notificationsPage.unreadPlural : t.notificationsPage.unread}`}
              className="shrink-0 rounded-full bg-purple-100 dark:bg-purple-900/50 px-2 py-0.5 text-xs font-semibold text-purple-700 dark:text-purple-300"
            >
              <AnimatedNumber value={unreadCount} />
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-neutral-400 dark:text-neutral-500">
          {relative
            ? `${t.notificationsPage.lastActivity} ${relative}`
            : t.notificationsPage.noActivity}
        </p>
        {handoffErr && <p className="mt-1 text-xs text-red-600">{handoffErr}</p>}
      </div>

      {isLive && (
        <button
          type="button"
          disabled={opening || !bu?.handoffApiPath}
          onClick={() => void openBU()}
          className="shrink-0 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-1.5 text-sm font-semibold text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/30 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
        >
          {opening ? t.opening : t.notificationsPage.openBU}
        </button>
      )}
    </li>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────

export function NotificationsPage(): React.ReactElement {
  const t = useT();
  const { data, loading, error, refetch } = useActivitySummary();

  if (loading && !data) {
    return (
      <>
        <h1 className="mb-4 text-xl font-bold text-neutral-900 dark:text-neutral-100">
          {t.notificationsPage.title}
        </h1>
        <div
          role="status"
          aria-live="polite"
          aria-label={t.notificationsPage.loading}
          className="space-y-3"
        >
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700"
            />
          ))}
        </div>
      </>
    );
  }

  if (error && !data) {
    return (
      <>
        <h1 className="mb-4 text-xl font-bold text-neutral-900 dark:text-neutral-100">
          {t.notificationsPage.title}
        </h1>
        <div className="rounded-2xl border border-red-200 bg-red-50 dark:bg-red-950/30 px-5 py-6">
          <p className="mb-3 text-sm font-medium text-red-700 dark:text-red-400">
            {t.notificationsPage.errorTitle}
          </p>
          <button
            type="button"
            onClick={refetch}
            className="rounded-xl bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          >
            {t.notificationsPage.retry}
          </button>
        </div>
      </>
    );
  }

  const busEntries = data
    ? Object.entries(data.bus).sort(([, a], [, b]) => {
        // Sort by most recent activity first
        const ta = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0;
        const tb = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0;
        return tb - ta;
      })
    : [];

  return (
    <FadeIn>
      <h1 className="mb-4 text-xl font-bold text-neutral-900 dark:text-neutral-100">
        {t.notificationsPage.title}
      </h1>

      {busEntries.length === 0 ? (
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-5 py-10 text-center shadow-sm">
          <div className="mb-3 text-4xl" aria-hidden="true">
            🔔
          </div>
          <h2 className="mb-1 font-semibold text-neutral-900 dark:text-neutral-100">
            {t.notificationsPage.emptyTitle}
          </h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {t.notificationsPage.emptyDetail}
          </p>
        </div>
      ) : (
        <StaggerList className="space-y-2" as="ul" aria-label={t.notificationsPage.title}>
          {busEntries.map(([buId, entry]) => (
            <StaggerItem key={buId} as="div">
              <BUActivityCard
                buId={buId}
                unreadCount={entry.unreadCount}
                lastActivityAt={entry.lastActivityAt}
              />
            </StaggerItem>
          ))}
        </StaggerList>
      )}
    </FadeIn>
  );
}
